import React from 'react'
import { useIntl } from 'react-intl'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'
import { ContentDownloadCode, populateCode, useYamlPath } from './common'
import { StepContent, WizardStepContext } from '../agent-wizard-steps'
import { formatStyles } from '../../../../react-intl-helpers'
import { OPERATING_SYSTEM } from '../common/constants'
import { getLanguageInfo } from '../common/utils'
import { StepsKubernetesConfigureOperator } from './steps-kubernetes-configure-operator'

// This file is mostly docker related steps but perhaps not exclusively
export const clusterAgentConnectionCode = `apiVersion: agents.contrastsecurity.com/v1beta1
kind: ClusterAgentConnection
metadata:
  name: default-agent-connection
  namespace: contrast-agent-operator
spec:
  template:
    spec:
      url: {agentUrl}
      apiKey:
        secretName: default-agent-connection-secret
        secretKey: apiKey
      serviceKey:
        secretName: default-agent-connection-secret
        secretKey: serviceKey
      userName:
        secretName: default-agent-connection-secret
        secretKey: userName`

const useColumns = () => {
  const intl = useIntl()

  return [
    {
      accessorKey: 'property',
      header: intl.formatMessage({ id: 'title_property' })
    },
    { accessorKey: 'type', header: intl.formatMessage({ id: 'title_type' }) },
    {
      accessorKey: 'required',
      header: intl.formatMessage({ id: 'title_required' })
    },
    {
      accessorKey: 'default',
      header: intl.formatMessage({ id: 'title_defaultValue' })
    },
    {
      accessorKey: 'desc',
      header: intl.formatMessage({ id: 'title_description' })
    }
  ]
}
const useTableData = () => {
  const intl = useIntl()

  return [
    {
      property: 'spec.namespace',
      type: 'string[]',
      required: intl.formatMessage({ id: 'action_no' }),
      default: intl.formatMessage({ id: 'detail_allNamespaces' }),
      desc: intl.formatMessage({ id: 'detail_specNamespaceDesc' })
    },
    {
      property: 'spec.template',
      type: 'AgentConfiguration',
      required: intl.formatMessage({ id: 'action_yes' }),
      default: '',
      desc: intl.formatMessage({ id: 'detail_specTemplateDesc' })
    }
  ]
}

const stepContainerRunInstaller = {
  titleMsg: 'title_runInstaller',
  Content: { message: 'detail_dotnetCoreRunInstaller' }
}

const stepContainerDotnetRunInstaller = {
  titleMsg: 'title_runInstaller',
  Content: { message: 'detail_dotnetRunInstaller' }
}

const stepContainerAddContrastConfigurationToImage = {
  titleMsg: 'title_addContrastConfigurationToImage',
  Content: () => {
    const ctx = React.useContext(WizardStepContext)
    const path = useYamlPath()
    const osType = ctx.branches[OPERATING_SYSTEM.KEY]
    const cmd = osType === OPERATING_SYSTEM.WINDOWS ? 'COPY' : 'cp'

    return (
      <StepContent
        step={{
          Content: {
            message: 'detail_addContrastConfigurationToImage',
            values: {
              code: `${cmd} contrast_security.yaml ${path}`
            }
          }
        }}
      />
    )
  }
}
const stepContainerBuildYourImage = {
  titleMsg: 'title_buildYourImage',
  Content: () => (
    <CodeBlock
      style={{ width: '100%' }}
      message={`docker build -t my-app-with-contrast .`}
    />
  )
}

const stepContainerCreateAnEnvFile = {
  titleMsg: 'title_createAnEnvironmentFile',
  Content: () => (
    <ContentDownloadCode
      message="detail_dotnetDockerCreateAnEnvironmentFile"
      codeTemplate={`CONTRAST__API__URL={agentUrl}
CONTRAST__API__API_KEY={api_key}
CONTRAST__API__SERVICE_KEY={service_key}
CONTRAST__API__USER_NAME={user_uid}`}
    />
  )
}

const stepContainerRunYourContainer = {
  titleMsg: 'title_runYourContainer',
  Content: () => (
    <CodeBlock
      style={{ width: '100%' }}
      message={`docker run -P my-app-with-contrast`}
    />
  )
}

const stepContainerInstallOperator = {
  defaultIsOpen: true,
  titleMsg: 'title_installOperator',
  Content: { message: 'detail_installOperator' }
}

const stepContainerConfigureOperator = {
  titleMsg: 'title_configureOperator',
  Content: () => <StepsKubernetesConfigureOperator />
}

const stepContainerInjectWorkloads = (language) => {
  const languageInfo = getLanguageInfo(language)
  return {
    titleMsg: 'title_injectWorkloads',
    Content: () => (
      <ContentDownloadCode
        values={{
          language: languageInfo?.agentId,
          languageTitle: languageInfo?.uiText
        }}
        message="detail_injectWorkloads"
        codeTemplate={`apiVersion: agents.contrastsecurity.com/v1beta1
kind: AgentInjector
metadata:
  name: ${languageInfo?.agentId}-contrast
  namespace: default
spec:
  type: ${languageInfo?.agentId}
  selector:
    labels:
      - name: contrast-${languageInfo?.agentId}-agent
        value: enabled`}
      />
    )
  }
}

const stepContainerClusterAgentAndAgent = {
  titleMsg: 'title_configureClusterAgentAndAgent',
  defaultIsOpen: false,
  Content: () => {
    const ctx = React.useContext(WizardStepContext)

    return (
      <>
        <StepContent
          step={{
            Content: {
              message: 'detail_configureClusterAgentAndAgentA',
              values: {
                code: `apiVersion: agents.contrastsecurity.com/v1beta1
kind: ClusterAgentConfiguration
metadata:
  name: default-agent-configuration
  namespace: contrast-agent-operator
spec:
  namespaces:
    - default
  template:
    spec:
      yaml: |
        server:
          environment: QA`,
                ...formatStyles.table({
                  columns: useColumns(),
                  data: useTableData()
                })
              }
            }
          }}
        />
        <StepContent
          step={{
            Content: {
              message: 'detail_configureClusterAgentAndAgentB',
              values: {
                code: populateCode(
                  clusterAgentConnectionCode,
                  ctx.apiCredentials
                ),
                ...formatStyles.table({
                  columns: useColumns(),
                  data: useTableData()
                })
              }
            }
          }}
        />
      </>
    )
  }
}

export {
  stepContainerRunInstaller,
  stepContainerDotnetRunInstaller,
  stepContainerAddContrastConfigurationToImage,
  stepContainerBuildYourImage,
  stepContainerCreateAnEnvFile,
  stepContainerRunYourContainer,
  stepContainerInstallOperator,
  stepContainerConfigureOperator,
  stepContainerInjectWorkloads,
  stepContainerClusterAgentAndAgent
}
